import type { XYPosition } from '@xyflow/react';

/**
 * Canvas note — a sticky-note-style annotation placed on the workflow canvas.
 */
export type Note = {
  content: string;
  color: string;
  metadata: {
    position: XYPosition;
    width: number;
    height: number;
  };
};

/**
 * Preset note color swatches with background and border colors for light and dark modes
 */
export const NOTE_COLORS: Record<string, {
  light: { background: string; border: string };
  dark: { background: string; border: string };
}> = {
  '#FFFBCC': {
    light: { background: '#FFFBCC', border: '#E8D89C' },
    dark: { background: '#4A4522', border: '#5C572E' },
  }, // Yellow
  '#CCE5FF': {
    light: { background: '#CCE5FF', border: '#9CC5E8' },
    dark: { background: '#1E3A5F', border: '#2A4A70' },
  }, // Blue
  '#CCFFCC': {
    light: { background: '#CCFFCC', border: '#8DD4A4' },
    dark: { background: '#1E4A2A', border: '#2A5C38' },
  }, // Green
  '#FFCCCC': {
    light: { background: '#FFCCCC', border: '#E8A8B0' },
    dark: { background: '#4A2222', border: '#5C2E2E' },
  }, // Red
  '#E0CCFF': {
    light: { background: '#E0CCFF', border: '#C4B8E0' },
    dark: { background: '#3A2A5F', border: '#4A3A70' },
  }, // Purple
  '#FFFFFF': {
    light: { background: '#FFFFFF', border: '#DEDEDE' },
    dark: { background: '#3A3A3A', border: '#4A4A4A' },
  }, // White/Gray
};

/** Array of color keys for iteration */
export const NOTE_COLOR_KEYS = Object.keys(NOTE_COLORS);

/** Helper to get note colors for the current theme */
export const getNoteColors = (colorKey: string, isDarkMode: boolean): { background: string; border: string } => {
  const colorConfig = NOTE_COLORS[colorKey];
  if (!colorConfig) {
    // Fallback to default yellow
    return isDarkMode
      ? { background: '#4A4522', border: '#5C572E' }
      : { background: '#FFFBCC', border: '#E8D89C' };
  }
  return isDarkMode ? colorConfig.dark : colorConfig.light;
};

/** Default note dimensions (px) - matches n8n style */
export const DEFAULT_NOTE_SIZE = {
  width: 240,
  height: 180,
  minWidth: 150,
  minHeight: 80,
} as const;

/** Default note color */
export const DEFAULT_NOTE_COLOR = '#FFFBCC';
